import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { shippingAccessScope } from '../../hooks/useShippingRequest';
import { readPurchaseReceipt } from '../distributor/distributorPurchaseReceipt';
import { moneyMinor } from '../../utils/money';

/** Purchase selection only. The parent owns the single durable bank operation and all writes. */
export default function DistributorWirePurchase({
  distributorId,
  entry,
  purchase,
  locked,
  disabled,
  onChange,
}) {
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [choice, setChoice] = useState(entry.purchaseId || '');
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const session = useRef(captureSession());
  const scope = useRef(shippingAccessScope());
  const mounted = useRef(false);
  const generation = useRef(0);
  const detailGeneration = useRef(0);
  const current = () =>
    mounted.current && isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      detailGeneration.current += 1;
    };
  }, []);
  useEffect(() => {
    if (locked || !current()) return;
    const request = ++generation.current;
    setRows(null);
    setError('');
    const load = async () => {
      try {
        const result = (await distributorService.adminPurchases(distributorId, page))?.data?.data;
        if (!current() || generation.current !== request) return;
        if (
          !Array.isArray(result?.data) ||
          result.page !== page ||
          result.limit !== 20 ||
          !Number.isSafeInteger(result.total) ||
          result.total < 0 ||
          result.data.length > 20
        )
          throw new Error('Invalid purchases');
        const valid = result.data.map((row) => readPurchaseReceipt({ data: { data: row } }));
        if (valid.some((row) => row.method !== 'wire' || row.status !== 'prepared' || !row.open))
          throw new Error('Invalid wire selection');
        if (page > Math.max(1, Math.ceil(result.total / 20))) {
          setPage(Math.max(1, Math.ceil(result.total / 20)));
          return;
        }
        setRows(valid);
        setTotal(result.total);
      } catch {
        if (current() && generation.current === request)
          setError('Prepared wire purchases are unavailable. Retry loading them.');
      }
    };
    load();
  }, [distributorId, page, retry, locked]);
  const choose = async (id) => {
    if (!current() || disabled || locked) return;
    const request = ++detailGeneration.current;
    setChoice(id);
    setSelected(null);
    setError('');
    onChange({ purchaseId: '', amountCents: undefined, currency: 'USD' });
    if (!id) {
      setOpening(false);
      return;
    }
    setOpening(true);
    try {
      const result = readPurchaseReceipt(await distributorService.adminPurchase(distributorId, id));
      if (!current() || detailGeneration.current !== request) return;
      if (
        result._id !== id ||
        result.method !== 'wire' ||
        result.status !== 'prepared' ||
        !result.open
      )
        throw new Error('Invalid wire purchase');
      setSelected(result);
      onChange({ purchaseId: id, amountCents: result.amountCents, currency: 'USD' });
    } catch {
      if (current() && detailGeneration.current === request)
        setError('This prepared wire purchase is unavailable. Reload and review it.');
    } finally {
      if (current() && detailGeneration.current === request) setOpening(false);
    }
  };
  const shown = locked ? purchase : selected;
  const move = (next) => {
    if (!current() || disabled || locked) return;
    detailGeneration.current += 1;
    setChoice('');
    setSelected(null);
    setOpening(false);
    generation.current += 1;
    setRows(null);
    onChange({ purchaseId: '', amountCents: undefined, currency: 'USD' });
    setPage(next);
  };
  return (
    <div className="space-y-3 sm:col-span-2" aria-label="Cleared wire purchase">
      <p className="text-sm text-slate-600">
        One cleared wire must match one saved purchase for its exact USD amount. Check cleared funds
        at the bank; a customer receipt is not proof of clearance.
      </p>
      {!locked && (
        <>
          {error && (
            <p role="alert">
              {error}{' '}
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (current()) setRetry((value) => value + 1);
                }}
              >
                Reload wire purchases
              </button>
            </p>
          )}
          {!rows && !error && <p role="status">Loading prepared wire purchases...</p>}
          {rows?.length === 0 && (
            <p>No open prepared wire purchases. The distributor must save a wire purchase first.</p>
          )}
          <label>
            Prepared wire purchase{' '}
            <select
              className="block w-full rounded border p-2"
              value={choice}
              disabled={disabled || !rows}
              onChange={(event) => choose(event.target.value)}
            >
              <option value="">Choose saved wire purchase</option>
              {rows?.map((row) => (
                <option key={row._id} value={row._id}>
                  {row.familyKey.replaceAll('_', ' ')} · {row.units.toLocaleString()} units ·{' '}
                  {moneyMinor(row.amountCents)} USD · {row._id}
                </option>
              ))}
            </select>
          </label>
          {total > 20 && (
            <nav aria-label="Prepared wire purchase pages" className="flex gap-3">
              <button
                type="button"
                disabled={disabled || !rows || page <= 1}
                onClick={() => {
                  move(page - 1);
                }}
              >
                Previous purchases
              </button>
              <span>
                Page {page} of {Math.ceil(total / 20)}
              </span>
              <button
                type="button"
                disabled={disabled || !rows || page >= Math.ceil(total / 20)}
                onClick={() => {
                  move(page + 1);
                }}
              >
                Next purchases
              </button>
            </nav>
          )}
        </>
      )}
      {opening && <p role="status">Checking saved wire purchase...</p>}
      {shown && (
        <div className="rounded border p-3 text-sm" role="region" aria-label="Frozen wire purchase">
          <p>Purchase ID: {shown._id}</p>
          <p>Product family: {shown.familyKey.replaceAll('_', ' ')}</p>
          <p>Units to credit: {shown.units.toLocaleString()}</p>
          <p>
            Exact cleared amount: <strong>{moneyMinor(shown.amountCents)} USD</strong>
          </p>
        </div>
      )}
      <label>
        Bank confirmation reference{' '}
        <input
          className="block w-full rounded border p-2"
          value={entry.sourceReference || ''}
          disabled={disabled || locked}
          maxLength={120}
          onChange={(event) => onChange({ sourceReference: event.target.value })}
        />
      </label>
      <label>
        Bank clearance date{' '}
        <input
          className="block rounded border p-2"
          type="date"
          value={entry.clearedAt || ''}
          disabled={disabled || locked}
          onChange={(event) => onChange({ clearedAt: event.target.value })}
        />
      </label>
    </div>
  );
}
